/**
 * Auto-advancing product gallery.
 *
 * Enhances <product-images> when the Product pages section uses the
 * "Slider - Auto-advancing" gallery. Two modes, matching how the theme already
 * renders the gallery in each viewport:
 *
 *   fader  (desktop thumbnails / mobile thumbs) - crossfades to the next media
 *   scroll (mobile slider)                      - scrolls the snap slider along
 *
 * It never fights the visitor: autoplay pauses on hover, focus, touch, when the
 * tab is hidden, when the gallery scrolls out of view, while the zoom lightbox
 * is open, and it never starts for visitors who prefer reduced motion.
 */
(function () {
  'use strict';

  const selectors = {
    gallery: 'product-images[data-autoplay-desktop], product-images[data-autoplay-mobile]',
    mediaItem: '[data-image-id]',
    mediaList: '[data-product-media-list]',
    lightbox: '.pswp--open, .pswp--visible',
  };

  const attributes = {
    autoplayDesktop: 'data-autoplay-desktop',
    autoplayMobile: 'data-autoplay-mobile',
    interval: 'data-autoplay-interval',
    faderDesktop: 'data-fader-desktop',
    faderMobile: 'data-fader-mobile',
  };

  const defaultInterval = 5000;
  const minInterval = 2000;
  const mobileBreakpoint = 750;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isMobile = () => {
    if (window.theme && typeof window.theme.isMobile === 'boolean') return window.theme.isMobile;
    return window.innerWidth < ((window.theme && window.theme.sizes && window.theme.sizes.small) || mobileBreakpoint);
  };

  class GalleryAutoplay {
    constructor(gallery) {
      this.gallery = gallery;
      this.mediaList = gallery.querySelector(selectors.mediaList);
      this.timer = null;
      this.interactionTimer = null;
      this.hovered = false;
      this.interacting = false;
      this.inView = true;

      const interval = parseInt(gallery.getAttribute(attributes.interval), 10);
      this.interval = Math.max(isNaN(interval) ? defaultInterval : interval, minInterval);

      this.onEnter = () => {
        this.hovered = true;
        this.evaluate();
      };
      this.onLeave = () => {
        this.hovered = false;
        this.evaluate();
      };
      this.onInteractionStart = () => {
        this.interacting = true;
        clearTimeout(this.interactionTimer);
        this.evaluate();
      };
      this.onInteractionEnd = () => {
        clearTimeout(this.interactionTimer);
        // Give the visitor the full interval on the media they just picked.
        this.interactionTimer = setTimeout(() => {
          this.interacting = false;
          this.evaluate();
        }, this.interval);
      };
      this.onVisibilityChange = () => this.evaluate();
      this.onResize = () => this.evaluate();

      this.listen();
      this.evaluate();
    }

    listen() {
      this.gallery.addEventListener('pointerenter', this.onEnter);
      this.gallery.addEventListener('pointerleave', this.onLeave);
      this.gallery.addEventListener('focusin', this.onEnter);
      this.gallery.addEventListener('focusout', this.onLeave);
      this.gallery.addEventListener('pointerdown', this.onInteractionStart);
      this.gallery.addEventListener('touchstart', this.onInteractionStart, {passive: true});
      document.addEventListener('pointerup', this.onInteractionEnd);
      document.addEventListener('pointercancel', this.onInteractionEnd);
      document.addEventListener('touchend', this.onInteractionEnd);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('theme:resize:width', this.onResize);
      window.addEventListener('resize', this.onResize);
      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', this.onResize);
      } else if (typeof reducedMotion.addListener === 'function') {
        reducedMotion.addListener(this.onResize);
      }

      if ('IntersectionObserver' in window) {
        // Threshold 0: galleries are often taller than the viewport, so "any part
        // of it is on screen" is the only meaningful test.
        this.observer = new IntersectionObserver(
          (entries) => {
            this.inView = entries[entries.length - 1].isIntersecting;
            this.evaluate();
          },
          {threshold: 0}
        );
        this.observer.observe(this.gallery);
      }
    }

    get slides() {
      return Array.from(this.gallery.querySelectorAll(selectors.mediaItem));
    }

    get isFader() {
      return isMobile() ? this.gallery.hasAttribute(attributes.faderMobile) : this.gallery.hasAttribute(attributes.faderDesktop);
    }

    get enabled() {
      const attribute = isMobile() ? attributes.autoplayMobile : attributes.autoplayDesktop;
      return this.gallery.hasAttribute(attribute);
    }

    get canPlay() {
      return (
        this.enabled &&
        this.inView &&
        !this.hovered &&
        !this.interacting &&
        !reducedMotion.matches &&
        !document.hidden &&
        !document.querySelector(selectors.lightbox) &&
        this.slides.length > 1
      );
    }

    evaluate() {
      this.canPlay ? this.play() : this.pause();
    }

    play() {
      if (this.timer) return;
      this.timer = setInterval(() => {
        // Conditions can change without an event we listen for (lightbox opening).
        if (!this.canPlay) {
          this.pause();
          return;
        }
        this.advance();
      }, this.interval);
    }

    pause() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      if (this.isFader) {
        if (typeof this.gallery.showNextImage === 'function') this.gallery.showNextImage();
        return;
      }

      this.scrollToNextSlide();
    }

    /**
     * Native scroll-snap slider: move the media list to the next slide's snap
     * position, wrapping back to the first.
     */
    scrollToNextSlide() {
      if (!this.mediaList) return;

      const slides = this.slides;
      const listRect = this.mediaList.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(this.mediaList).paddingLeft) || 0;
      const snapLeft = listRect.left + padding;

      let currentIndex = 0;
      let closest = Infinity;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - snapLeft);
        if (distance < closest) {
          closest = distance;
          currentIndex = index;
        }
      });

      const nextIndex = (currentIndex + 1) % slides.length;

      if (nextIndex === 0) {
        this.mediaList.scrollTo({left: 0, behavior: 'smooth'});
        return;
      }

      const offset = slides[nextIndex].getBoundingClientRect().left - snapLeft;
      this.mediaList.scrollTo({left: this.mediaList.scrollLeft + offset, behavior: 'smooth'});
    }
  }

  const init = (root) => {
    (root || document).querySelectorAll(selectors.gallery).forEach((gallery) => {
      if (gallery.galleryAutoplay) return;
      gallery.galleryAutoplay = new GalleryAutoplay(gallery);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
